//Hooks
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
//Actions
import * as act from "../../store/actions/anonHomeForm";
import { setCanAdvance } from "../../store/actions/anonymousForms";
//Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Wizard from "../shared/wizard/Wizard";
import YourInformation from "./shared-pages/YourInformation";
import IncidentLocation from "./shared-pages/IncidentLocation";
import IncidentDetails from "./shared-pages/IncidentDetails";
import DateTime from "./shared-pages/DateTime";
import StatusBar from "../ui/status-bar/StatusBar";
import Witnesses from "./shared-pages/Witnesses";
import OtherProperty from "./home-pages/OtherProperty";
import Injury from "./home-pages/Injury";
import Documents from "./shared-pages/Documents";
import HomeClaimSummary from "./home-pages/HomeClaimSummary";
//Styles, Constants
import Styles from "./AnonForms.module.css";
import useDocuments from "../../hooks/useDocuments";
import { BehalfOfOptions, damageTypes, userTypes, yesNoUnsure } from "../../constants/options";
import { alphabeticTest, emailLengthProps, emailTest, freeFormTest, length100Props, length1333Props, length500Props, length50Props, noSpecialsTest, phoneProps, phoneTest, trueFalseRequired, zipProps, zipTest } from "../../utilities/validationUtilities";
import { Canada, US } from "../../constants/data";
import inputUtilities from "../../utilities/forms/formInputUtilities";
import { confirmEmailValidator, createValidator, timeValidator, validateFieldset, validatePersonArray } from "../../utilities/forms/formValidationUtilities";
import { textToBoolean } from "../../utilities/dataUtilities";
import { colors } from "../../constants/colors";

function AnonHomeForm() {
  const dispatch = useDispatch();
  const canAdvance = useSelector((state) => state.anonymousForms.canAdvance);
  const lossCauses = useSelector((state) => state.forms.lossCauses);
  const config = useSelector((state) => state.app.config);
  const documents = useSelector((state) => state.anonHomeForm.documents);
  const [page, setPage] = useState(0);

  const handleDocuments = useDocuments(documents, act.setHomeDocuments, true);

  const rows3Props = { rows: 3 };

  const fields = {
    contactFirstName: {
      label: "First Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    contactLastName: {
      label: "Last Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    contactEmail: {
      label: "Email",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: emailTest,
      additionalProps: emailLengthProps,
    },
    confirmEmail: {
      label: "Confirm Email",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: emailTest,
      additionalProps: emailLengthProps,
      fieldName: "confirmEmail",
    },
    contactPhone: {
      label: "Phone",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: phoneTest,
      additionalProps: phoneProps,
    },
    userType: {
      label: "I am",
      value: userTypes[0].value,
      required: true,
      page: 0,
      editPage: 2,
    },
    onBehalfOf: {
      label: "On Behalf Of",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
    },
    behalfOfFirst: {
      label: "First Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    behalfOfLast: {
      label: "Last Name",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    whereDate: {
      label: "Date",
      value: "",
      required: true,
      page: 1,
      editPage: 0,
      additionalProps: { min:"1950-01-01", max: new Date().toLocaleDateString("fr-ca") }
    },
    whereTime: {
      label: "Time",
      value: "",
      required: true,
      page: 1,
      editPage: 0,
      fieldName: "whereTime",
    },
    whereAddress: {
      label: "Address",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whereCity: {
      label: "City",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereState: {
      label: "State",
      value: "",
      page: 2,
      editPage: 3,
      required: true,
    },
    whereZipcode: {
      label: "Zip Code",
      value: "",
      page: 2,
      editPage: 3,
      regex: zipTest,
      additionalProps: zipProps,
    },
    whereCounty: {
      label: "County",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereCountry: {
      label: "Country",
      value: US.name,
      page: 2,
      editPage: 3,
    },
    whatHappen: {
      label: "Describe the incident here",
      value: "",
      page: 3,
      editPage: 4,
      required: true,
      additionalProps: { rows: 12, ...length1333Props },
      regex: freeFormTest,
    },
    damageType: {
      label: "Who sustained the damage?",
      value: damageTypes[0].label,
      page: 3,
      editPage: 4,
      required: true,
    },
    lossCause: {
      label: "Which of the following best describes the cause of loss?",
      value: "",
      displayValue: null,
      page: 3,
      editPage: 4,
      required: true,
    },
    wasAnyoneInjured: {
      label: "Was Anyone Injured?",
      value: yesNoUnsure[2].value,
      page: 4,
      editPage: 6,
    },
    injuredFirstName: {
      label: "First Name",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredLastName: {
      label: "Last Name",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredAddress: {
      label: "Address",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    injuredCity: {
      label: "City",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredState: {
      label: "State",
      value: "",
      page: 4,
      editPage: 6,
    },
    injuredCounty: {
      label: "County",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredZipcode: {
      label: "Zip Code",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: zipProps,
      regex: zipTest
    },
    injuryDescription: {
      label: "Description of injuries",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: {...rows3Props, ...length1333Props },
      regex: freeFormTest,
    },
    wasTreatmentProvided: {
      label: "Was medical treatment provided?",
      value: false,
      page: 4,
      editPage: 6,
    },
    medicalProviderFirstName: {
      label: "First Name",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: freeFormTest,
    },
    medicalProviderLastName: {
      label: "Last Name",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: freeFormTest,
    },
    medicalProviderAddress: {
      label: "Address",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    medicalProviderCity: {
      label: "City",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    medicalProviderState: {
      label: "State",
      value: "",
      page: 4,
      editPage: 6,
    },
    medicalProviderZipcode: {
      label: "Zip Code",
      value: "",
      page: 4,
      editPage: 6,
      additionalProps: zipProps,
      regex: zipTest,
    },
    witnessesPresent: {
      label: "Were there any witnesses?",
      value: yesNoUnsure[2].value,
      page: 5,
      editPage: 5,
    },
    damageToProperty: {
      label: "Was there damage to another property?",
      value: yesNoUnsure[2].value,
      required: true,
      page: 6,
      editPage: 7,
    },
    whoOwnsProperty: {
      label: "Do you know the owner of the property?",
      value: false,
      required: true,
      page: 6,
      editPage: 7,
      fieldName: "whoOwnsProperty",
      validation: trueFalseRequired,
    },
    propertyOwnerFirstName: {
      label: "First Name",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerLastName: {
      label: "Last Name",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
    },
    propertyOwnerAddress: {
      label: "Address",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyOwnerCity: {
      label: "City",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerState: {
      label: "State",
      value: "",
      page: 6,
      editPage: 7,
    },
    propertyOwnerCounty: {
      label: "County",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerZipcode: {
      label: "Zip Code",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: zipProps,
      regex: zipTest,
    },
    propertyOwnerPhone: {
      label: "Phone",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    propertyOwnerDescribeProperty: {
      label: "Describe the property",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerDamageProperty: {
      label: "Describe the damage to the property",
      value: "",
      page: 6,
      editPage: 7,
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerEstimateOfDamage: {
      label: "Estimate of damage",
      value: "",
      page: 6,
      editPage: 7,
    },
  };

  const witness = {
    firstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    witnessPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    }
  };

  let [witnessInfo, setWitnessInfo] = useState([]);
  const [formFields, setFormFields] = useState(fields);
  const [validatorsAdded, setValidatorsAdded] = useState();
  const utilities = inputUtilities(formFields, setFormFields, Styles);

  const addValidatorsNames = (theFields, setFields, shouldDispatch = false) => {
    let newFields = { ...theFields };
    for (let key in newFields) {
      if (
        newFields[key].fieldName !== formFields.confirmEmail.fieldName &&
        newFields[key].fieldName !== formFields.whereTime.fieldName &&
        newFields[key].fieldName !== formFields.whoOwnsProperty.fieldName
      ) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          fieldName: key,
          validation: createValidator({
            ...theField.additionalProps,
            ...theField.regex,
            label: theField.label,
            required: theField.required,
          }),
        };
      } else if (newFields[key].fieldName === formFields.confirmEmail.fieldName) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          validation: confirmEmailValidator(newFields[key], formFields.contactEmail.value, formFields.confirmEmail.label),
        };
      } else if (newFields[key].fieldName === formFields.whereTime.fieldName) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          validation: timeValidator(newFields[key], formFields.whereDate.value),
        };
      }
    }
    shouldDispatch ? dispatch(setFields(newFields)) : setFields(newFields);
  };

  useEffect(() => {
    if (formFields && !validatorsAdded) {
      addValidatorsNames(formFields, setFormFields);
      addValidatorsNames(witness, act.setWitnessObj, true);
      setValidatorsAdded(true);
    }
  }, [formFields, validatorsAdded])

  const reValidate = (field) => field.validation && field.value && utilities.handleFieldChange(field.value, field, true);

  useEffect(() => {
      formFields.confirmEmail.validation = confirmEmailValidator(formFields.confirmEmail, formFields.contactEmail.value, formFields.confirmEmail.label);
  }, [formFields.contactEmail.value])

  useEffect(() => {
      formFields.whereTime.validation = timeValidator(formFields.whereTime, formFields.whereDate.value);
  }, [formFields.whereDate.value])

  useEffect(() => {
      reValidate(formFields.confirmEmail);
  }, [formFields.confirmEmail.validation])

  useEffect(() => {
      reValidate(formFields.whereTime);
  }, [formFields.whereTime.validation])

  useEffect(() => {
      if (validatePage()) {
        dispatch(setCanAdvance(true));
      } else {
        dispatch(setCanAdvance(false));
      }
  }, [formFields, witnessInfo, page])

  useEffect(() => {
    if (validatorsAdded) {
      const theFields = {...formFields};
      if (formFields.whereCountry.value === Canada.name) {
        theFields.whereState.label = "Province";
      } else {
        theFields.whereState.label = "State";
      }
      theFields.whereState.value = "";
      setFormFields(theFields);
    }
  },[formFields.whereCountry.value, validatorsAdded])//handles change in province/state label based on location country
  
  const validatePage = (editPage = null, displayErrors = false) => {
    let pageFields = Object.values(formFields)
      .map((obj, index) => {
        return { ...obj, index: index };
      });
    if (editPage) {
      pageFields = pageFields.filter((field) => field.editPage === editPage);
    } else {
      pageFields = pageFields.filter((field) => field.page === page);
    }
      if (page === 5 || editPage === 5) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors) 
          && validatePersonArray(witnessInfo, setWitnessInfo, displayErrors);
      }
      if (formFields.userType.value !== userTypes[1].value) {
        pageFields = pageFields.filter(field => 
          field.fieldName !== formFields.onBehalfOf.fieldName && 
          field.fieldName !== formFields.behalfOfLast.fieldName
        );
      } else if (formFields.onBehalfOf.value !== BehalfOfOptions[1]) {
        pageFields = pageFields.filter(field => field.fieldName !== formFields.behalfOfLast.fieldName);
      }
      if (!textToBoolean(formFields.damageToProperty.value) || !formFields.whoOwnsProperty.value) {
        pageFields = pageFields.filter(field => 
          field.fieldName !== formFields.propertyOwnerLastName.fieldName && 
          field.fieldName !== formFields.propertyOwnerDescribeProperty.fieldName && 
          field.fieldName !== formFields.propertyOwnerDamageProperty.fieldName
        );
      }
      return validateFieldset(pageFields, formFields, setFormFields, displayErrors);
    };

  // Wizard page handling

  const next = () => {
    setPage(Math.min(page + 1, lastPage));
  };
  const previous = () => {
    setPage(Math.max(0, page - 1));
  };

  const pages = [
    <YourInformation
      key="yourInfo"
      fields={formFields}
      utilities={utilities}
    />,
    <DateTime
      key="dateTime"
      fields={formFields}
      utilities={utilities}
    />,
    <IncidentLocation
      key="location"
      fields={formFields}
      utilities={utilities}
    />,
    <IncidentDetails
      key="details"
      lossCauses={lossCauses}
      fields={formFields}
      utilities={utilities}
    />,
    <Injury
      key="injury"
      fields={formFields}
      utilities={utilities}
    />,
    <Witnesses
      key="witnesses"
      witnesses={witnessInfo}
      fields={formFields}
      utilities={utilities}
      setWitnesses={setWitnessInfo}
    />,
    <OtherProperty
      key="otherProperty"
      fields={formFields}
      utilities={utilities}
    />,
    <Documents
      key="documents"
      documents={documents}
      handleDocuments={handleDocuments}
      config={config}
    />,
    <HomeClaimSummary
      key="summary"
      handleDocuments={handleDocuments}
      config={config}
      fields={formFields}
      utilities={utilities}
      validatePage={validatePage}
      witnesses={witnessInfo}
      setWitnesses={setWitnessInfo}
      setFields={setFormFields}
    />,
  ];
  var lastPage = pages.length - 1;
  // end wizard page handling

  return (
    <div className={Styles.container}>
      <div className={`${Styles.backArrowContainer} ${Styles.arrowContainer}`}>
        <button
          type="button"
          className={Styles.arrowButton}
          onClick={previous}
          aria-label="Previous Page"
          disabled={page === 0}
        >
          <div
            className={
              page !== 0
                ? Styles.arrowCircle
                : ` ${Styles.arrowCircle} ${Styles.disabledArrow}`
            }
          >
            <FontAwesomeIcon
              className={`${Styles.arrow} ${Styles.backArrow}`}
              icon={faChevronLeft}
              size="2x"
            />
          </div>
          <p
            className={
              page !== 0
                ? Styles.svgText
                : `${Styles.svgText} ${Styles.disabledSvgText}`
            }
          >
            BACK
          </p>
        </button>
      </div>
      <div className={Styles.screenBox}>
        <span className="redMessage">
          Fields marked with a (*) are required
        </span>{" "}
        <br />
        <Wizard page={page}>{pages}</Wizard>
        <div className={Styles.buttonDiv}>
          <div className={Styles.navButton}>
            {page !== 0 && (
              <button
                type="button"
                className={page === 0 ? Styles.noArrow : Styles.arrowButton}
                onClick={previous}
                aria-label="Next Page"
              >
                <div className={Styles.arrowCircle}>
                  <FontAwesomeIcon
                    className={`${Styles.bottomArrow} ${Styles.backArrow}`}
                    icon={faChevronLeft}
                    size="2x"
                  />
                </div>
                <span className={Styles.svgText}>BACK</span>
              </button>
            )}
          </div>
          <div className={Styles.navButton}>
            {page !== lastPage && (
              <button
                type="button"
                className={
                  page === lastPage ? Styles.noArrow : Styles.arrowButton
                }
                disabled={canAdvance ? false : true}
                onClick={next}
                aria-label="Next Page"
              >
                <span
                  className={
                    canAdvance
                      ? Styles.svgText
                      : `${Styles.svgText} ${Styles.disabledSvgText}`
                  }
                >
                  NEXT
                </span>
                <div
                  className={
                    canAdvance
                      ? Styles.arrowCircle
                      : ` ${Styles.arrowCircle} ${Styles.disabledArrow}`
                  }
                >
                  <FontAwesomeIcon
                    className={`${Styles.bottomArrow} ${Styles.nextArrow}`}
                    icon={faChevronRight}
                    size="2x"
                  />
                </div>
              </button>
            )}
          </div>
        </div>
        <div className={Styles.statusBarDiv}>
          <StatusBar
            bgcolor={colors.primary}
            completed={page + 1}
            total={lastPage + 1}
            message={"Claim Progress"}
          />
        </div>
      </div>
      <div className={`${Styles.nextArrowContainer} ${Styles.arrowContainer}`}>
        <button
          type="button"
          className={page === lastPage ? Styles.noArrow : Styles.arrowButton}
          disabled={canAdvance ? false : true}
          onClick={next}
          aria-label="Next Page"
        >
          <div
            className={
              canAdvance
                ? Styles.arrowCircle
                : ` ${Styles.arrowCircle} ${Styles.disabledArrow}`
            }
          >
            <FontAwesomeIcon
              className={`${Styles.arrow} ${Styles.nextArrow}`}
              icon={faChevronRight}
              size="2x"
            />
          </div>
          <p
            className={
              canAdvance
                ? Styles.svgText
                : `${Styles.svgText} ${Styles.disabledSvgText}`
            }
          >
            NEXT
          </p>
        </button>
      </div>
    </div>
  );
}

export default AnonHomeForm;
